import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/context/ThemeContext";
import { Layout } from "@/components/Layout";
import Index from "./pages/Index";
import LoginPage from "./pages/auth/Login";
import DashboardPage from "./pages/Dashboard";
import CustomerProfilePage from "./pages/customer_profile/CustomerProfile";
import LoansPage from "./pages/actions/loan/Loans";
import UsersPage from "./pages/users/UsersPage";
import SettingsPage from "./pages/settings/Settings";
import AuditTrailsPage from "./pages/AuditTrails";
import ReportsPage from "./pages/report/Reports";
import PlaceholderPage from "./pages/PlaceholderPage";
import NotFound from "./pages/NotFound";
import LeadsPage from "./pages/actions/LeadsPage";
import LoansDueTodayPage from "./pages/actions/due/LoansDueTodayPage";
import CustomersPage from "./pages/actions/customer/CustomersPage";
import CollectionCalendarPage from "./pages/calendar/CollectionCalendarPage";
import DisbursedCalendarPage from "./pages/calendar/DisbursedCalendarPage";
import AuthGuard from "./components/AuthGuard";
import { MpesaCollectionPage } from "./pages/mpesa/MpesaCollectionPage";
import { MpesaUnResolvedCollectionPage } from "./pages/mpesa/MpesaUnResolvedCollectionPage";
import { CoPendingLoanPage } from "./pages/actions/loan/CoPendingLoanPage";
import { PendingLoanPage } from "./pages/actions/loan/PendingLoanPage";
import { PendingDisbursementLoanPage } from "./pages/actions/loan/PendingDisbursementLoanPage";
import DefaultedLoanPage from "./pages/actions/loan/DefaultedLoanPage";
import TargetsPage from "./pages/TargetsPage";
import LoanDueNextPage from "./pages/actions/due/LoanDueNextPage";
import InstallmentDueTodayPage from "./pages/actions/due/InstallmentDueTodayPage";
import DefaultedInstallmentPage from "./pages/actions/due/DefaultedInstallmentPage";
import PaymentBreakdownPage from "./pages/management/PaymentBreakdownPage";
import ProfitabilityMatrixPage from "./pages/management/ProfitabilityMatrixPage";
import MonthlyPerfomancePage from "./pages/management/MonthlyPerfomancePage";
import IncompleteCustomerProfile from "./pages/actions/customer/IncompleteCustomerProfile";
import PendingCustomerProfile from "./pages/actions/customer/PendingCustomerProfile";
import RejectedCustomerProfile from "./pages/actions/customer/RejectedCustomerProfile";
import MoveCustomerProfilePage from "./pages/actions/customer/MoveCustomerProfile";
import AddCustomerWizardPage from "./pages/actions/new_customer/AddCustomerWizardPage";
import ProfitLossPage from "./pages/management/ProfitLossPage";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import BulkSMSPage from "./pages/actions/BulkSMSPage";
import ExpensesPage from "./pages/actions/ExpensesPage";
import RolesPage from "./pages/users/RolesPage";
import ChartsAccountsPage from "./pages/settings/ChartsAccountsPage";
import BranchesPage from "./pages/settings/BranchesPage";
import LoanProductsPage from "./pages/settings/LoanProductsPage";
import PairingsPage from "./pages/pairing/PairingsPage";
import CoaCategoriesPage from "./pages/settings/CoaCategoriesPage";
import { RequirePerm } from "./components/auth/RequirePerm";
import ActivityPage from "./pages/ActivityPage";
import TargetsPlanner from "./pages/TargetsPlanner";
import LoansRevenuePage from "./pages/report/pages/LoansRevenue";
import LoanDefaultersPage from "./pages/report/pages/LoanDefaulters";
import DormantCustomersPage from "./pages/report/pages/DormantCustomers";
import BranchPartialPaymentsPage from "./pages/report/pages/BranchPartialPayments";
import PerformanceReportPage from "./pages/report/pages/PerformanceReport";
import { QueuedDisbursementPage } from "./pages/actions/loan/QueuedDisbursement";
import ResetPasswordPage from "./pages/reset-password/ResetPasswordPage";
import DefaultedLoanReportPage from "./pages/report/pages/LoanDefaulters";
import LeadsReportPage from "./pages/report/pages/Leads";
import NewCustomersReportPage from "./pages/report/pages/NewCustomers";
import CustomersReportPage from "./pages/report/pages/Customers";
import DormantCustomersReportPage from "./pages/report/pages/DormantCustomers";
import SetNewPasswordPage from "./pages/reset-password/SetNewPassword";



const queryClient = new QueryClient();

const App = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const dir = i18n.dir(); // "ltr" or "rtl"
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", i18n.language);

    // Optional: helper class for CSS tweaks
    document.documentElement.classList.toggle("rtl", dir === "rtl");
  }, [i18n.language, i18n]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            {/* your Routes exactly as-is */}
            <Routes>
              {/* PUBLIC ROUTE */}
              <Route path="/" element={<LoginPage />} />
              <Route path="/login" element={<LoginPage />} />
               <Route path="/set-password" element={<SetNewPasswordPage />} />
             

              {/* 🔐 PROTECTED ROUTES */}
              <Route
                element={
                  <AuthGuard>
                    <Layout />
                  </AuthGuard>
                }
              >
                <Route path="/home" element={<Index />} />
                <Route path="/dashboard" element={
                  <RequirePerm perm="dashboard">
                    <DashboardPage />
                  </RequirePerm>
                } />

                <Route path="/actions/leads" element={
                  <RequirePerm perm="view-lead">
                    <LeadsPage />
                  </RequirePerm>
                }
                />

                <Route path="/actions/bulk-sms" element={
                  <RequirePerm perm="messenger"><BulkSMSPage /></RequirePerm>

                } />
                <Route path="/actions/expenses" element={
                  <RequirePerm perm="view-expenses"><ExpensesPage /></RequirePerm>

                } />

                <Route path="/customer-profile" element={
                  <RequirePerm perm="customers-index"> <CustomerProfilePage /></RequirePerm>

                } />

                {/* mpesa records */}
                <Route path="/mpesa/collections" element={
                  <RequirePerm perm="mpesa-records"> <MpesaCollectionPage /></RequirePerm>

                } />
                <Route path="/mpesa/unresolved-collections" element={
                  <RequirePerm perm="unresolved-mpesa-records"> <MpesaUnResolvedCollectionPage /></RequirePerm>

                } />

                {/* calendar */}
                <Route path="/calendar/collections" element={
                  <RequirePerm perm="co-calendar"><CollectionCalendarPage /></RequirePerm>

                } />
                <Route path="/calendar/disbursement" element={
                  <RequirePerm perm="dis-calendar"> <DisbursedCalendarPage /></RequirePerm>

                } />

                {/* customers */}
                <Route path="/actions/customers/incomplete-profile" element={

                  <IncompleteCustomerProfile />
                } />
                <Route path="/actions/customers/pending-customers" element={

                  <PendingCustomerProfile />
                } />
                <Route path="/actions/customers/customers" element={

                  <CustomersPage />
                } />
                <Route path="/actions/customers/rejected-customers" element={

                  <RejectedCustomerProfile />
                } />
                <Route path="/actions/customers/move-customers-profile" element={
                  <RequirePerm perm="move-customers"><MoveCustomerProfilePage /></RequirePerm>

                }
                />

                {/* loan dues */}
                <Route path="/actions/due/today" element={

                  <LoansDueTodayPage />
                } />
                <Route path="/actions/due/due-next-3days" element={

                  <LoanDueNextPage />
                } />
                <Route path="/actions/due/installment-due-today" element={

                  <InstallmentDueTodayPage />
                } />
                <Route path="/actions/due/defaulted-installment" element={

                  <DefaultedInstallmentPage />
                } />

                {/* loans */}
                <Route path="/actions/loans/active" element={

                  <LoansPage />
                } />
                <Route path="/actions/loans/defaulted" element={

                  <DefaultedLoanPage />
                } />
                <Route path="/actions/loans/pending-disbursement" element={

                  <PendingDisbursementLoanPage />
                } />
                <Route path="/actions/loans/queued-disbursement" element={

                  <QueuedDisbursementPage />
                } />
                <Route path="/actions/loans/pending" element={

                  <PendingLoanPage />
                } />
                <Route path="/actions/loans/co" element={

                  <CoPendingLoanPage />
                } />

                {/* management report */}
                <Route path="/management/monthly-performance" element={
                  <RequirePerm perm="management-reports"><MonthlyPerfomancePage /></RequirePerm>

                } />
                <Route path="/management/payment-breakdown" element={
                  <RequirePerm perm="management-reports"> <PaymentBreakdownPage /></RequirePerm>

                } />
                <Route path="/management/profit-matrix" element={
                  <RequirePerm perm="management-reports"> <ProfitabilityMatrixPage /></RequirePerm>

                } />
                <Route path="/management/profit-loss" element={
                  <RequirePerm perm="management-reports"> <ProfitLossPage /></RequirePerm>

                } />

                {/* new customer */}
                <Route path="/actions/new-customer" element={
                  <RequirePerm perm="customers-add"><AddCustomerWizardPage /></RequirePerm>

                } />
                {/* targets */}
                <Route path="/targets" element={
                  <RequirePerm perm="branch-target"> <TargetsPage /></RequirePerm>

                } />
                {/* reports */}
                <Route path="/reports" element={
                  <RequirePerm perm="reports"><ReportsPage /></RequirePerm>

                } />
                {/* loans reports */}
                <Route path="/reports/loans-revenue" element={
                  <RequirePerm perm="reports"><LoansRevenuePage /></RequirePerm>

                } />
                <Route path="/reports/loan-defaulters" element={
                  <RequirePerm perm="reports"><LoanDefaultersPage /></RequirePerm>

                } />

                {/* customers reports */}
                <Route path="/reports/dormant-customers" element={
                  <RequirePerm perm="reports"><DormantCustomersReportPage /></RequirePerm>

                } />

                {/* payments reports */}
                <Route path="/reports/branch-partial-payments" element={
                  <RequirePerm perm="reports"><BranchPartialPaymentsPage /></RequirePerm>

                } />

                {/* Perfomamance analysis reports */}
                <Route path="/reports/best-performing-branch" element={
                  <RequirePerm perm="reports"><PerformanceReportPage /></RequirePerm>

                } />

                <Route path="/reports/loan-defaulters" element={
                  <RequirePerm perm="reports"><DefaultedLoanReportPage /></RequirePerm>

                } />

                 <Route path="/reports/leads" element={
                  <RequirePerm perm="reports"><LeadsReportPage /></RequirePerm>

                } />

                 <Route path="/reports/new-customers" element={
                  <RequirePerm perm="reports"><NewCustomersReportPage /></RequirePerm>

                } />

                <Route path="/reports/customers" element={
                  <RequirePerm perm="reports"><CustomersReportPage /></RequirePerm>

                } />

                {/* users */}
                <Route path="/users/all" element={
                  <RequirePerm perm="users"> <UsersPage /></RequirePerm>

                } />
                <Route path="/users/roles" element={
                  <RequirePerm perm="users"> <RolesPage /></RequirePerm>

                } />
                {/* pair */}
                <Route path="/pairing" element={
                  <RequirePerm perm="pair"><PairingsPage /></RequirePerm>

                } />

                {/* settings */}
                <Route path="/settings/all" element={
                  <RequirePerm perm="settings"> <SettingsPage /></RequirePerm>

                } />
                <Route path="/settings/charts" element={
                  <RequirePerm perm="settings">  <ChartsAccountsPage /></RequirePerm>

                } />
                <Route path="/settings/branches" element={
                  <RequirePerm perm="settings"><BranchesPage /></RequirePerm>

                } />
                <Route path="/settings/chart-of-account-categories" element={
                  <RequirePerm perm="settings"><CoaCategoriesPage /></RequirePerm>

                } />

                <Route path="/settings/loan-products" element={
                  <RequirePerm perm="settings"><LoanProductsPage /></RequirePerm>

                } />

                {/* audit trail */}
                <Route path="/audit-trails" element={
                  <RequirePerm perm="dashboard"><AuditTrailsPage /></RequirePerm>

                } />
                <Route path="/activity" element={<ActivityPage />} />
                <Route path="/target-planner" element={<TargetsPlanner />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />

              </Route>





              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;